/**
 * Gain-share style efficiency projection for Portfolio Diagnostic.
 *
 * ProjectedAnnualOperationalAlpha =
 *   Units × TurnoverRate × AvgSavings − Units × TurnoverRate × 25
 *
 * TurnoverRate = implied maintenance/turnover cycles per unit per year,
 * derived from reported friction hours (no extra user field).
 */

const ORCHESTRATION_FEE = 25;

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

/**
 * @param {number} units
 * @param {number} frictionHoursPerMonth
 * @returns {number} cycles per unit per year (bounded)
 */
export function computeTurnoverRate(units, frictionHoursPerMonth) {
  const u = Math.max(1, Number(units) || 1);
  const h = Math.max(0, Number(frictionHoursPerMonth) || 0);
  const annualHours = h * 12;
  const portfolioCycles = Math.max(1, annualHours / 36);
  const r = portfolioCycles / u;
  return clamp(r, 0.2, 4);
}

/**
 * Expected average negotiated savings below ceiling per dispatch (heuristic).
 * @param {number} autoCommitCeiling
 */
export function computeAvgSavings(autoCommitCeiling) {
  const c = Math.max(50, Number(autoCommitCeiling) || 150);
  return clamp(c * 0.18, 35, 220);
}

/**
 * @param {{ units: number; frictionHoursPerMonth: number; autoCommitCeiling: number }} p
 * @returns {{ projectedAnnualOperationalAlpha: number; turnoverRate: number; avgSavings: number }}
 */
export function computeOperationalAlpha(p) {
  const units = Math.max(1, Math.round(Number(p.units) || 1));
  const frictionHoursPerMonth = Math.max(0, Number(p.frictionHoursPerMonth) || 0);
  const autoCommitCeiling = Math.max(50, Number(p.autoCommitCeiling) || 150);
  const turnoverRate = computeTurnoverRate(units, frictionHoursPerMonth);
  const avgSavings = computeAvgSavings(autoCommitCeiling);
  const gross = units * turnoverRate * avgSavings;
  const fees = units * turnoverRate * ORCHESTRATION_FEE;
  const projectedAnnualOperationalAlpha = Math.round(gross - fees);
  return {
    projectedAnnualOperationalAlpha,
    turnoverRate: Math.round(turnoverRate * 1000) / 1000,
    avgSavings: Math.round(avgSavings * 100) / 100,
  };
}

export const PROVISIONING_STORAGE_KEY = "clumse_operator_provisioning_v1";

/**
 * @param {object} payload
 * @param {string} payload.operatorId
 * @param {object} payload.diagnostic
 * @param {object[]} payload.demoEntries
 */
export function persistOperatorProvisioning(payload) {
  try {
    sessionStorage.setItem(
      PROVISIONING_STORAGE_KEY,
      JSON.stringify({ v: 1, ...payload })
    );
  } catch {
    /* quota / private mode */
  }
}

/**
 * @returns {object | null}
 */
export function readOperatorProvisioning() {
  try {
    const raw = sessionStorage.getItem(PROVISIONING_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * @param {object} d
 * @param {string} operatorId
 * @returns {object[]} ActionLogFeed-compatible rows
 */
export function buildDemoProtocolEntries(d, operatorId) {
  const shortId = String(operatorId || "").replace(/-/g, "").slice(0, 12);
  const u = Math.round(Number(d.units) || 1);
  const h = Math.round(Number(d.frictionHoursPerMonth) || 0);
  const ceil = Math.round(Number(d.autoCommitCeiling) || 150);
  const alpha = Math.round(Number(d.projectedAnnualOperationalAlpha) || 0);
  const r = d.turnoverRate ?? computeTurnoverRate(u, h);
  const as = d.avgSavings ?? computeAvgSavings(ceil);

  return [
    {
      id: "diag_prov",
      protocol: "OperatorProvisioning",
      state: "REGISTERED",
      step: "REGISTERED",
      status: "ok",
      message: `OPERATOR_ID ${shortId} · provisioning handshake complete.`,
      at: "T-00:00:00",
      processingLatencyMs: 6,
      agentId: "agt_prov_01",
    },
    {
      id: "diag_scale",
      protocol: "PortfolioDiagnostic",
      state: "INTAKE",
      step: "INTAKE",
      status: "auto_committed",
      message: `Asset instances: ${u} · friction: ${h}h/mo · auto-commit ceiling $${ceil} · implied R=${r}/u/yr · model est. $${as}/evt (illustrative).`,
      at: "T-00:00:01",
      processingLatencyMs: 11,
      agentId: "agt_diag_02",
    },
    {
      id: "diag_alpha",
      protocol: "GainShareModel",
      state: "PROJECTION",
      step: "PROJECTION",
      status: "ok",
      message: `Illustrative annual model output $${alpha.toLocaleString()} (internal estimate only - not a promise of results).`,
      at: "T-00:00:02",
      processingLatencyMs: 9,
      agentId: "agt_alpha_03",
    },
  ];
}
